import CloseIcon from "@mui/icons-material/Close";
import { Box, Button } from "@mui/material";
import { Base64 } from "js-base64";
import React, { useEffect, useRef, useState } from "react";

import { FullInput } from "@/components/Utils.tsx";
import {
  MISSING_EXTENSION_MESSAGE,
  normalizeUserSuppliedExtension,
  renameFileWithExtension,
  resolveUploadMimeParts,
  truncate,
} from "@/utils";
import { FileWithMetaType, parseFile, preventDefaults } from "./Generics";

export default function TusHooksDropZone({
  setUploadData,
  text,
  autoClick,
  sx,
}: {
  setUploadData: (data: FileWithMetaType) => void;
  text?: string | React.ReactNode;
  autoClick?: boolean;
  sx?: React.CSSProperties;
}) {
  const dropZoneText = text !== undefined ? text : "Click or Drag & Drop to Upload File";
  const [titleAndDescription, setTitleAndDescription] = useState<{
    title: string;
    description: string;
  }>({
    title: "",
    description: "",
  });
  const [disabled, setDisabled] = useState<boolean>(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const dropZone = useRef<HTMLDivElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [parsedFileData, setParsedFileData] = useState<FileWithMetaType | null>(null);
  const [extensionInput, setExtensionInput] = useState<string>("");
  const titleCharLimit = 40;
  const descriptionCharLimit = 500;

  const tooLongTitle = titleAndDescription?.title.length > titleCharLimit;
  const tooLongDescription = titleAndDescription?.description.length > descriptionCharLimit;

  const reset = () => {
    setDisabled(false);
    setParsedFileData(null);
    setTitleAndDescription({ title: "", description: "" });
    setExtensionInput("");
    setUploadData(null);
    setErrorMessage(null);
    if (fileInput?.current) {
      fileInput.current.value = "";
    }
  };

  const applySuppliedExtension = (data: FileWithMetaType | null): FileWithMetaType | null => {
    if (!data?.meta?.requiresExtension) {
      return data;
    }
    const normalized = normalizeUserSuppliedExtension(extensionInput);
    if (!normalized) {
      return data;
    }
    const renamedFile = renameFileWithExtension(data.file, normalized);
    const mimeParts = resolveUploadMimeParts(renamedFile);
    return {
      ...data,
      file: renamedFile,
      meta: {
        ...data.meta,
        filename: renamedFile.name,
        name: truncate(renamedFile.name, 30, "..."),
        extension: normalized,
        requiresExtension: false,
        requiresTitleAndDescription: mimeParts.type === "audio",
        filetype: mimeParts.filetype,
        type: mimeParts.type,
        codec: mimeParts.codec,
      },
    };
  };

  /**
   * @todo: Fix base64 double encoding issue. Work with the backend to resolve this properly.
   *   - Future reference if anyone needs to figure out base64. It looks like we are double encoding the data and the backend is doing some sort of check for double encoding
   *   - tus automatically base64 encodes the metadata before sending it to the server, so the double encoding here is not necessary, but since the backend double-decodes it's required
   *   - Places to fix: ScheduleMessageFileUpload.tsx, FileUploadModal.tsx, WorkflowModalData.tsx
   */
  const setUploadDataAndDisableState = () => {
    setDisabled(true);
    const withExtension = applySuppliedExtension(parsedFileData);
    const includeTitle = withExtension?.meta?.requiresTitleAndDescription === true && !!titleAndDescription?.title;
    const parsedWithUpdatedMeta = includeTitle
      ? {
          ...withExtension,
          meta: {
            ...withExtension?.meta,
            title: Base64.encode(titleAndDescription.title),
            description: Base64.encode(titleAndDescription.description),
          },
        }
      : withExtension;
    setUploadData(parsedWithUpdatedMeta);
    setTimeout(() => {
      reset();
    }, 1000);
  };

  const mutateParsedFileData = ({ key, value }: { key: string; value: string }) => {
    if (!parsedFileData?.file) {
      return;
    }
    setTitleAndDescription((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDropZoneClick = () => {
    if (parsedFileData) {
      return;
    }
    fileInput?.current?.click();
  };

  const parseAndSetFileData = async (file: File) => {
    const response = await parseFile(file);
    if (response?.meta) {
      setParsedFileData(response);
    } else {
      setErrorMessage("Invalid File, please try again.");
      reset();
    }
  };

  // @todo enable this after next release
  // useEffect(() => {
  //   if (autoClick) {
  //     fileInput?.current?.click();
  //   }
  // }, [autoClick]);

  /**
   * Handle Dropped Files
   */
  useEffect(() => {
    if (!dropZone?.current) {
      return;
    }

    const highlight = () => dropZone.current?.classList.add("highlight");
    const unhighlight = () => dropZone.current?.classList.remove("highlight");
    const handleDrop = (e: DragEvent) => {
      const dt = e?.dataTransfer;
      const files = dt?.files;
      if (files?.length > 0) {
        parseAndSetFileData(files[0]);
      } else {
        setErrorMessage("No files detected, please try again.");
      }
    };

    ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
      dropZone.current.addEventListener(eventName, preventDefaults, false);
      document.body.addEventListener(eventName, preventDefaults, false);
    });

    ["dragenter", "dragover"].forEach((eventName) => {
      dropZone.current.addEventListener(eventName, highlight, false);
    });

    ["dragleave", "drop"].forEach((eventName) => {
      dropZone.current.addEventListener(eventName, unhighlight, false);
    });

    dropZone.current.addEventListener("drop", handleDrop, false);

    return () => {
      ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
        dropZone?.current?.removeEventListener(eventName, preventDefaults, false);
        document?.body?.removeEventListener(eventName, preventDefaults, false);
      });

      ["dragenter", "dragover"].forEach((eventName) => {
        dropZone?.current?.removeEventListener(eventName, highlight, false);
      });

      ["dragleave", "drop"].forEach((eventName) => {
        dropZone?.current?.removeEventListener(eventName, unhighlight, false);
      });

      dropZone?.current?.removeEventListener("drop", handleDrop, false);
    };
  }, []);

  const dropzoneStyles = parsedFileData?.meta
    ? {
        border: "solid 2px #fff",
      }
    : {
        border: "dashed 2px #999",
        "&.highlight": {
          backgroundColor: "#1d1f21",
          borderColor: "#2c9b4d",
          cursor: "wait",
        },
      };

  const selectionAfterExtension = parsedFileData ? applySuppliedExtension(parsedFileData) : null;
  const previewMimeParts =
    selectionAfterExtension?.file != null ? resolveUploadMimeParts(selectionAfterExtension.file) : null;
  const requiresTitle = previewMimeParts?.type === "audio";
  const requiresTitleAndDoesntHaveTitle = requiresTitle && !titleAndDescription?.title;
  const requiresExtension = parsedFileData?.meta?.requiresExtension === true;
  const normalizedExtension = normalizeUserSuppliedExtension(extensionInput);
  const requiresExtensionAndDoesntHaveOne = requiresExtension && !normalizedExtension;
  const showUploadButton = (!requiresTitle || !requiresTitleAndDoesntHaveTitle) && !requiresExtensionAndDoesntHaveOne;

  const HelperText = ({ characterLimit, field }) => {
    return (
      <Box>
        {field?.length}/{characterLimit}
      </Box>
    );
  };

  if (errorMessage) {
    return <Box sx={{ color: "red" }}>{errorMessage}</Box>;
  }

  return (
    <Box sx={{ ...sx }}>
      {requiresTitle && (
        <Box sx={{ mb: 2 }}>
          <FullInput
            label="Title"
            disabled={disabled}
            sx={{
              mb: 0,
              position: "relative",
            }}
            value={titleAndDescription.title ?? ""}
            callback={(e) => mutateParsedFileData({ value: e?.target?.value, key: "title" })}
            error={titleAndDescription.title.length > titleCharLimit}
          />
          <Box sx={{ mb: 1, fontSize: "12px", textAlign: "right" }}>
            <HelperText characterLimit={titleCharLimit} field={titleAndDescription.title} />
          </Box>

          <FullInput
            disabled={disabled}
            id="activeTemplate"
            multiline
            label="Description"
            rows={2}
            sx={{ mb: "-5px", position: "relative" }}
            value={titleAndDescription?.description ?? ""}
            callback={(e) =>
              mutateParsedFileData({
                value: e?.target?.value,
                key: "description",
              })
            }
            error={titleAndDescription?.description.length > descriptionCharLimit}
          />

          <Box sx={{ fontSize: "12px", textAlign: "right" }}>
            <HelperText characterLimit={descriptionCharLimit} field={titleAndDescription.description} />
          </Box>
        </Box>
      )}
      <Box
        sx={{
          borderRadius: "1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: "1rem",
          minHeight: "120px",
          ...dropzoneStyles,
        }}
        ref={dropZone}
        onClick={handleDropZoneClick}
      >
        {parsedFileData?.meta?.name ? (
          <Box sx={{ width: "100%" }}>
            <Box>
              <button
                className="basic-button"
                style={{
                  display: "flex",
                  gap: 1,
                  padding: "0.25rem 0.5rem",
                  width: "100%",
                  alignItems: "center",
                }}
                disabled={disabled}
                onClick={reset}
              >
                <Box
                  sx={{
                    color: "#ccc",
                    flexGrow: 1,
                    fontSize: "14px",
                    justifyContent: "flex-start",
                  }}
                >
                  {parsedFileData?.meta?.name}
                </Box>
                <Box className="basic-button">
                  <CloseIcon sx={{ fontSize: "14px" }} />
                </Box>
              </button>
            </Box>
            {requiresExtension && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ color: "red", fontSize: "12px", mb: 1 }}>{MISSING_EXTENSION_MESSAGE}</Box>
                <FullInput
                  id="file-extension-input"
                  label="File extension"
                  disabled={disabled}
                  placeholder="e.g. jpeg"
                  sx={{ mb: 0 }}
                  value={extensionInput}
                  callback={(e) => setExtensionInput(e?.target?.value ?? "")}
                  error={!!extensionInput && !normalizedExtension}
                />
                {!!extensionInput && !normalizedExtension && (
                  <Box sx={{ color: "red", fontSize: "12px", mt: 0.5 }}>Extension must be 1–10 letters or digits.</Box>
                )}
              </Box>
            )}
            {showUploadButton && (
              <Button
                sx={{ mt: 1 }}
                disabled={disabled || (requiresTitle && (tooLongTitle || tooLongDescription))}
                onClick={setUploadDataAndDisableState}
                variant="contained"
              >
                Upload
              </Button>
            )}
          </Box>
        ) : (
          <Box>{dropZoneText}</Box>
        )}
      </Box>
      <input
        style={{ display: "none" }}
        disabled={disabled}
        ref={fileInput}
        type="file"
        onChange={(e) => parseAndSetFileData(e?.target?.files?.item(0))}
      />
    </Box>
  );
}
